import React from "react";

import LoadingSpinner from "js/common/views/loading-spinner";
import ErrorMsg from "js/common/views/error";
import TargetsList from "js/oneview/targets/targets-list";
import TargetCreator from "js/oneview/targets/add-new-target";
import useBooleanState from "js/common/hooks/use-boolean-state";
import {useTargets} from "js/oneview/targets/hooks/useTargets";
import {useTargetsEditor} from "js/oneview/targets/hooks/useTargetsEditor";
import {useTargetNotes} from "js/oneview/targets/hooks/useTargetNotes";
import TargetsButtons from "js/oneview/targets/targets-buttons";
import {betterMemoWithRef} from "js/common/utils/more-memo";

const TargetsWithRef = React.forwardRef(({kpiId, timeframe, qualifierType, qualifierId}, ref) => {
      const {
        targets,
        setTargets,
        isLoading,
        loadTargetsError,
        loadTargets
      } = useTargets(kpiId, qualifierType, qualifierId);

      const {
        targetsViewType,
        hasUnsavedChanges,
        isSaving,
        updateError,
        errorsByTargetId,
        updateTarget,
        saveTargets,
        startEditing,
        cancelEditing
      } = useTargetsEditor(kpiId, targets, setTargets);

      const {
        selectedTargetId,
        isAddingNote,
        addNoteError,
        selectTarget,
        deselectTarget,
        addNote
      } = useTargetNotes(targets, setTargets, targetsViewType);

      const {
        flag: isTargetCreatorOpen,
        setTrue: openTargetCreator,
        setFalse: closeTargetCreator
      } = useBooleanState(false);

      const handleTargetCreated = React.useCallback(() => {
        closeTargetCreator();
        loadTargets();
      }, [closeTargetCreator, loadTargets]);

      const handleCancelEditing = React.useCallback(() => {
        cancelEditing(loadTargets);
      }, [cancelEditing, loadTargets]);

      React.useImperativeHandle(ref, () => ({
        hasUnsavedChanges: () => hasUnsavedChanges,
        save: () => saveTargets(),
        discard: () => handleCancelEditing()
      }), [hasUnsavedChanges, saveTargets, handleCancelEditing]);

      if (isLoading) {
        return (
            <div style={{margin: "3rem"}}>
              <LoadingSpinner iconSize={3} label="Loading Targets" />
            </div>
        );
      }

      if (loadTargetsError) {
        return <ErrorMsg text={loadTargetsError} />;
      }

      return (
          <div data-test-id="targets-ct" style={{display: "flex", flexDirection: "column", height: "100%"}}>
            {!isTargetCreatorOpen && (
                <TargetsButtons
                    viewType={targetsViewType}
                    targets={targets}
                    isSaving={isSaving}
                    hasUnsavedChanges={hasUnsavedChanges}
                    onCancel={handleCancelEditing}
                    onEdit={startEditing}
                    onSave={saveTargets}
                    onAdd={openTargetCreator} />)}

            {updateError && <ErrorMsg text={updateError} />}

            {isTargetCreatorOpen && (
                <TargetCreator
                    kpiId={kpiId}
                    qualifierType={qualifierType}
                    qualifierId={qualifierId}
                    onNewTargetCreated={handleTargetCreated}
                    onCloseRequest={closeTargetCreator} />)}

            <TargetsList
                viewType={targetsViewType}
                timeframe={timeframe}
                selectedTargetId={selectedTargetId}
                targets={targets}
                onTargetClick={selectTarget}
                onCloseNotesRequest={deselectTarget}
                onAddTargetNoteRequest={addNote}
                onTargetChange={updateTarget}
                errorsByTargetId={errorsByTargetId}
                isUpdatingNotes={isAddingNote}
                addTargetNoteError={addNoteError} />
          </div>);
    }
);

const Targets = betterMemoWithRef({displayName: "TargetsCT"}, TargetsWithRef);

export default Targets;
